/**
 * Widget MZK - Inicializador Automático
 * Detecta configuração em window.WidgetConfig e inicializa widget automaticamente
 * 
 * Usa dynamic imports para carregar apenas os módulos necessários (code splitting)
 */

import widgetModularService from './services/WidgetModularService.js';
import envConfig from './config/env.js';

class MzkWidgetLoader {
    constructor() {
        this.modules = new Map();
        // Instâncias criadas via API pública standalone (ex: renderOfertaFormularioStandalone),
        // chaveadas pelo elemento container — permite múltiplas instâncias simultâneas do
        // mesmo tipo de módulo na página, ao contrário de `this.modules` (chaveado por nome
        // de componente, pensado para um único Widget Modular por página).
        this.standaloneModules = new Map();
        this.initialized = false;
    }

    /**
     * Resolve a lista de configurações de widget disponíveis na página.
     * Suporta:
     * - window.WidgetConfigs (array)
     * - window.WidgetConfig (objeto único)
     * - múltiplos containers com id banner-widget-*
     * @private
     */
    resolveWidgetConfigs() {
        if (typeof window === 'undefined') {
            return [];
        }

        const widgetConfigs = Array.isArray(window.WidgetConfigs)
            ? window.WidgetConfigs.filter(cfg => cfg && cfg.id)
            : [];

        if (widgetConfigs.length > 0) {
            return widgetConfigs;
        }

        const singleConfig = window.WidgetConfig && window.WidgetConfig.id
            ? window.WidgetConfig
            : null;

        const containerConfigs = Array.from(document.querySelectorAll('[id^="banner-widget-"]'))
            .map((el) => ({
                ...(singleConfig || {}),
                id: el.id.replace('banner-widget-', '')
            }))
            .filter(cfg => !!cfg.id);

        if (containerConfigs.length > 0) {
            return containerConfigs;
        }

        return singleConfig ? [singleConfig] : [];
    }

    /**
     * Inicializa widget baseado em window.WidgetConfig
     */
    async init() {
        if (this.initialized) {
            return;
        }

        const configs = this.resolveWidgetConfigs();
        if (configs.length === 0) {
            return;
        }

        const processedIds = new Set();

        for (const config of configs) {
            if (!config?.id || processedIds.has(config.id)) {
                continue;
            }

            processedIds.add(config.id);

            try {
                // Busca widget do Strapi
                const widgetData = await this.fetchWidgetData(config.id, config);

                // Renderiza componentes ativos
                await this.renderComponents(widgetData, config);
            } catch (error) {
                console.error(`[MzkWidgetLoader] Erro ao inicializar widget ${config.id}:`, error);
            }
        }

        this.initialized = true;
    }

    /**
     * Busca dados do widget do Strapi
     * @private
     */
    async fetchWidgetData(documentId, config) {
        // Se a config não especificar componentes, ativa todos para detecção automática
        const componentConfig = {
            ofertaFormulario: config.ofertaFormulario !== false,
            formulario_simples: config.formulario_simples !== false,
            minimal_data: config.minimal_data !== false,
            bloco_imagem_texto: config.bloco_imagem_texto !== false,
            cards: config.cards !== false,
            editor_html: config.editor_html !== false,
            marcas: config.marcas !== false,
            footer: config.footer !== false
        };

        return await widgetModularService.fetchWidget(documentId, componentConfig);
    }

    /**
     * Detecta automaticamente quais componentes têm dados válidos
     * @private
     */
    hasValidData(data) {
        if (!data) return false;
        if (Array.isArray(data) && data.length === 0) return false;
        if (typeof data === 'string' && data.trim() === '') return false;

        // Para objetos, verifica se tem dados reais
        if (typeof data === 'object') {
            if ('selectOptions' in data && Array.isArray(data.selectOptions)) {
                return data.selectOptions.length > 0;
            }
            // Se é um objeto com propriedade 'marcas' (padrão MarcasModule)
            if ('marcas' in data && Array.isArray(data.marcas)) {
                return data.marcas.length > 0; // Só válido se marcas[] não está vazio
            }
            // Se é um objeto com propriedade 'items' (padrão Carousel)
            if ('items' in data && Array.isArray(data.items)) {
                return data.items.length > 0;
            }
            // Para outros objetos, verifica se tem keys
            return Object.keys(data).length > 0;
        }

        return true;
    }

    /**
     * Renderiza componentes ativos
     * @private
     */
    async renderComponents(widgetData, config) {
        const containerId = `banner-widget-${config.id}`;
        const container = document.getElementById(containerId);

        if (!container) {
            console.error('[renderComponents] Container não encontrado:', containerId);
            return;
        }

        const { components } = widgetData;

        // Renderiza Bloco Imagem/Texto (se tiver dados válidos)
        if (this.hasValidData(components.bloco_imagem_texto)) {
            await this.renderBlocoImagemTexto(container, components.bloco_imagem_texto);
        }

        // Renderiza Cards (Carousel) (se tiver dados válidos)
        if (this.hasValidData(components.cards)) {
            await this.renderCarousel(container, components.cards);
        }

        // Renderiza Oferta Formulário (se tiver dados válidos)
        if (this.hasValidData(components.ofertaFormulario)) {
            // Verifica se a oferta está publicada (não é rascunho)
            const isDraft = components.ofertaFormulario.oferta?.is_draft ||
                components.ofertaFormulario.oferta?.isDraft ||
                components.ofertaFormulario.is_draft ||
                components.ofertaFormulario.isDraft ||
                false;

            if (isDraft) {
                console.warn('⚠️ [renderComponents] Oferta em rascunho - formulário não será exibido');
            } else {
                await this.renderOfertaFormulario(container, components.ofertaFormulario);
            }
        }

        // Renderiza Formulário Simples (se tiver dados válidos)
        if (this.hasValidData(components.formularioSimples)) {
            await this.renderFormularioSimples(container, components.formularioSimples);
        }

        // Renderiza Minimal Data (se tiver dados válidos)
        if (this.hasValidData(components.minimal_data)) {
            await this.renderMinimalData(container, components.minimal_data);
        }

        // Renderiza Marcas (se tiver dados válidos)
        if (this.hasValidData(components.marcas)) {
            await this.renderMarcas(container, components.marcas);
        }

        // Renderiza Editor HTML (se tiver dados válidos)
        if (this.hasValidData(components.editor_html)) {
            await this.renderEditorHTML(container, components.editor_html);
        }

        // Renderiza Footer (se tiver dados válidos)
        if (this.hasValidData(components.footer)) {
            await this.renderFooter(container, components.footer);
        }
    }

    /**
     * Renderiza módulo Bloco Imagem/Texto
     * @private
     */
    async renderBlocoImagemTexto(container, data) {
        try {
            // Dynamic import: carrega apenas se necessário
            const { default: BlocoImagemTextoModule } = await import('./modules/BlocoImagemTextoModule.js');

            // Injeta estilos do módulo (apenas uma vez)
            this.injectModuleStyles('bloco-texto', BlocoImagemTextoModule.getStyles());

            const blocoDiv = document.createElement('div');
            blocoDiv.className = 'mzk-bloco-wrapper';
            container.appendChild(blocoDiv);

            const module = new BlocoImagemTextoModule({
                data: data,
                container: blocoDiv
            });

            await module.init();
            module.render(blocoDiv);

            this.modules.set('bloco_imagem_texto', module);
        } catch (error) {
            console.error('[renderBlocoImagemTexto] Erro ao renderizar:', error);
        }
    }

    /**
     * Renderiza módulo Carousel
     * @private
     */
    async renderCarousel(container, cards) {
        try {
            // Dynamic import: carrega apenas se necessário
            const { default: CarouselModule } = await import('./modules/CarouselModule.js');

            // Injeta estilos do módulo (apenas uma vez)
            this.injectModuleStyles('carousel', CarouselModule.getStyles());

            const carouselDiv = document.createElement('div');
            carouselDiv.className = 'mzk-carousel-wrapper';
            container.appendChild(carouselDiv);

            // Converte cards para formato do CarouselModule
            const items = cards.map(card => ({
                id: card.id,
                title: card.titulo,
                description: card.categoria,
                link: card.link,
                buttonText: card.texto_botao,
                image: card.image || 'https://via.placeholder.com/1200x400?text=Sem+Imagem'
            }));

            const module = new CarouselModule({
                container: carouselDiv,
                items: items,
                autoplay: envConfig.get('CAROUSEL_AUTOPLAY_DELAY') > 0,
                autoplayDelay: envConfig.get('CAROUSEL_AUTOPLAY_DELAY')
            });

            await module.init();

            module.render(carouselDiv);

            this.modules.set('carousel', module);
        } catch (error) {
            console.error('[renderCarousel] Erro ao renderizar carousel:', error);
        }
    }

    /**
     * Renderiza módulo Marcas
     * @private
     */
    async renderMarcas(container, data) {
        try {
            // Dynamic import: carrega apenas se necessário
            const { default: MarcasModule } = await import('./modules/MarcasModule.js');

            const marcasDiv = document.createElement('div');
            marcasDiv.className = 'mzk-marcas-wrapper';
            container.appendChild(marcasDiv);

            const module = new MarcasModule({
                data: data,
                container: marcasDiv,
                injectStyles: true,
                animateIn: true
            });

            await module.init();
            module.render(marcasDiv);

            this.modules.set('marcas', module);
        } catch (error) {
            console.error('[renderMarcas] Erro ao renderizar:', error);
        }
    }

    /**
     * Renderiza módulo Editor HTML
     * @private
     */
    async renderEditorHTML(container, data) {
        try {
            // Dynamic import: carrega apenas se necessário
            const { default: EditorHTMLModule } = await import('./modules/EditorHTMLModule.js');

            // Injeta estilos do módulo (apenas uma vez)
            this.injectModuleStyles('editor-html', EditorHTMLModule.getStyles());

            const editorDiv = document.createElement('div');
            editorDiv.className = 'mzk-editor-html-wrapper';
            container.appendChild(editorDiv);

            const module = new EditorHTMLModule({
                data: data,
                container: editorDiv
            });

            await module.init();
            await module.render(editorDiv);

            this.modules.set('editor_html', module);
        } catch (error) {
            console.error('[renderEditorHTML] Erro ao renderizar:', error);
        }
    }

    /**
     * Renderiza módulo Footer
     * @private
     */
    async renderFooter(container, data) {
        try {
            // Dynamic import: carrega apenas se necessário
            const { default: FooterModule } = await import('./modules/FooterModule.js');

            // Injeta estilos do módulo (apenas uma vez)
            this.injectModuleStyles('footer', FooterModule.getStyles());

            const footerDiv = document.createElement('div');
            footerDiv.className = 'mzk-footer-wrapper';
            container.appendChild(footerDiv);

            const module = new FooterModule({
                data: data,
                container: footerDiv
            });

            await module.init();
            module.render(footerDiv);

            this.modules.set('footer', module);
        } catch (error) {
            console.error('[renderFooter] Erro ao renderizar:', error);
        }
    }

    /**
     * Renderiza módulo Oferta Formulário
     * @private
     */
    async renderOfertaFormulario(container, data) {
        try {
            // Dynamic import: carrega apenas se necessário
            const { default: OfertaFormularioModule } = await import('./modules/OfertaFormularioModule.js');

            const ofertaDiv = document.createElement('div');
            ofertaDiv.className = 'mzk-oferta-formulario-wrapper';
            container.appendChild(ofertaDiv);

            // OfertaFormularioModule usa Shadow DOM, então não precisa de injeção de estilos externa
            const module = new OfertaFormularioModule({
                ...data,
                container: ofertaDiv
            });

            await module.init();
            module.render(ofertaDiv);

            this.modules.set('ofertaFormulario', module);
        } catch (error) {
            console.error('❌ [renderOfertaFormulario] Erro ao renderizar:', error);
        }
    }

    /**
     * Renderiza módulo Formulário Simples
     * @private
     */
    async renderFormularioSimples(container, data) {
        try {
            const { default: FormularioSimplesModule } = await import('./modules/FormularioSimplesModule.js');

            const formularioDiv = document.createElement('div');
            formularioDiv.className = 'mzk-formulario-simples-wrapper';
            container.appendChild(formularioDiv);

            const module = new FormularioSimplesModule({
                ...data,
                container: formularioDiv
            });

            await module.init();
            module.render(formularioDiv);

            this.modules.set('formularioSimples', module);
        } catch (error) {
            console.error('❌ [renderFormularioSimples] Erro ao renderizar:', error);
        }
    }

    /**
     * Renderiza módulo Minimal Data
     * @private
     */
    async renderMinimalData(container, data) {
        try {
            const { default: FormularioSimplesModule } = await import('./modules/FormularioSimplesModule.js');

            const minimalDataDiv = document.createElement('div');
            minimalDataDiv.className = 'mzk-minimal-data-wrapper';
            container.appendChild(minimalDataDiv);

            const module = new FormularioSimplesModule({
                ...data,
                container: minimalDataDiv
            });

            await module.init();
            module.render(minimalDataDiv);

            this.modules.set('minimal_data', module);
        } catch (error) {
            console.error('❌ [renderMinimalData] Erro ao renderizar:', error);
        }
    }

    /**
     * Injeta estilos CSS dos módulos
     * @private
     */
    injectStyles() {
        // Removido - cada módulo agora injeta seus próprios estilos quando é carregado
    }

    /**
     * Injeta estilos de um módulo específico
     * @private
     */
    injectModuleStyles(moduleId, styles) {
        const styleId = `mzk-${moduleId}-styles`;

        if (document.getElementById(styleId)) {
            return; // Já foi injetado
        }

        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = styles;
        document.head.appendChild(style);
    }

    /**
     * Destrói todos os módulos
     */
    destroy() {
        this.modules.forEach((module) => {
            try {
                module.destroy();
            } catch (error) {
                // Silenciar erro
            }
        });

        this.modules.clear();
        this.initialized = false;
    }

    /**
     * API pública para consumidores externos que já resolveram a oferta por conta
     * própria (ex: notícias no Next.js, via cascata modelo/marca/featured/random) e só
     * precisam do template de formulário (form-2-steps / form-no-cep / padrao) — sem
     * depender de buscar um Widget Modular por documentId no Strapi (ver `init()`/
     * `fetchWidgetData()`, que são o fluxo baseado em `window.WidgetConfig`).
     *
     * @param {string|HTMLElement} containerId - id do elemento host no DOM, ou o próprio elemento
     * @param {object} data - mesmo shape de `oferta_formulario` (oferta, modelos, color, colorBackground, ...)
     * @returns {Promise<OfertaFormularioModule|null>}
     */
    async renderOfertaFormularioStandalone(containerId, data) {
        const container =
            typeof containerId === 'string'
                ? document.getElementById(containerId)
                : containerId;

        if (!container) {
            console.error('[MzkWidgetLoader] Container não encontrado:', containerId);
            return null;
        }

        const existing = this.standaloneModules.get(container);
        if (existing) {
            return existing;
        }

        try {
            const { default: OfertaFormularioModule } = await import('./modules/OfertaFormularioModule.js');

            const ofertaDiv = document.createElement('div');
            ofertaDiv.className = 'mzk-oferta-formulario-wrapper';
            container.appendChild(ofertaDiv);

            const module = new OfertaFormularioModule({
                ...data,
                container: ofertaDiv,
            });

            await module.init();
            module.render(ofertaDiv);

            this.standaloneModules.set(container, module);
            return module;
        } catch (error) {
            console.error('❌ [renderOfertaFormularioStandalone] Erro ao renderizar:', error);
            return null;
        }
    }

    /**
     * Destrói uma instância criada via `renderOfertaFormularioStandalone`.
     * @param {string|HTMLElement} containerId
     */
    destroyStandalone(containerId) {
        const container =
            typeof containerId === 'string'
                ? document.getElementById(containerId)
                : containerId;

        const module = this.standaloneModules.get(container);
        if (!module) return;

        try {
            module.destroy();
        } catch (error) {
            // Silenciar erro
        }

        this.standaloneModules.delete(container);
    }
}

// Auto-inicialização quando DOM estiver pronto
if (typeof window !== 'undefined') {
    const loader = new MzkWidgetLoader();

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            loader.init();
        });
    } else {
        // DOM já está pronto
        loader.init();
    }

    // Exporta para acesso global
    window.MzkWidget = loader;
}

export default MzkWidgetLoader;
